import LabeledSelect from 'components/LabeledSelect';
import { useEffect, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import DeviceCustomInput from './DeviceCustomInput';

const DeviceGroup = ({ setData, data, setError }) => {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const [deviceGroupsList, setDeviceGroupsList] = useState([]);

  useEffect(() => {
    if (deviceGroups) {
      const controllerGroups = deviceGroups.filter((gp) => gp.controllers?.length > 0);
      setDeviceGroupsList(controllerGroups);
    }
  }, [deviceGroups]);

  const dataMaps = {
    aware: {
      flood: {
        depth1: { name: 'Sensor Depth', units: 'Inches', type: 'float' },
        sensordepths: { name: 'Actual Depths', units: 'Inches', type: 'float' },
        depthDet1: { name: 'Depth Threshold 1', units: 'Inches', type: 'float' },
        depthDet2: { name: 'Depth Threshold 2', units: 'Inches', type: 'float' },
        depthDet3: { name: 'Depth Threshold 3', units: 'Inches', type: 'float' },
        airTemp: { name: 'Air Temperature', units: '°C', type: 'float' },
        baro: { name: 'Pressure', units: 'mBar', type: 'float' },
        battery: { name: 'Battery', units: 'Voltage', type: 'float' },
        depthInd: { name: 'Depth Detection Indicator (Threshold Met)', type: 'bool' },
        drInd: { name: 'Drop Rate Indicator (Threshold Met)', type: 'bool' },
        dropSDI: {
          name: 'Device Drop Count (SDI-12) Tipping bucket',
          units: 'Drops',
          type: 'float',
        },
        soilSDI: {
          name: 'Soil Moisture Reading (SDI-12)',
          units: 'ADC (Analog to Digital Converter)',
          type: 'float',
        },
        soilTempSDI: { name: 'Soil Temperature Reading (SDI-12)', units: '°C', type: 'float' },
        ffi1: { name: 'Flash Flood Indicator', type: 'bool' },
        h2oTemp: { name: 'Water Temperature', units: '°C', type: 'float' },
        rssi: { name: 'Signal Strength', units: 'dB', type: 'float' },
      },
    },
    'rescalert devices': {
      'manual road closure': {
        status: {
          name: 'Device Status',
          type: 'bool',
          values: { true: 'Closed', false: 'Open' },
        },
      },
      'technology bridge_flashing lights': {
        status: {
          name: 'Device Status',
          type: 'bool',
          values: { true: 'Closed', false: 'Open' },
        },
      },
      'technology bridge_siren/giant voice': {
        status: {
          name: 'Device Status',
          type: 'bool',
          values: { true: 'Alerting', false: 'Idle' },
        },
      },
    },
  };

  const [group, setGroup] = useState();
  const [systemType, setSystemType] = useState();
  const [deviceType, setDeviceType] = useState();
  const [deviceSubType, setDeviceSubType] = useState();

  const AWARErangesAndSteps = {
    depth1: { range: [0, 100], step: 1 },
  };

  useEffect(() => {
    if (
      data?.group === undefined ||
      data?.keyToCheck === undefined ||
      data?.keyToCompare === undefined ||
      data?.operator === undefined ||
      data?.valueToCompare === undefined ||
      data?.selectedDevice === undefined ||
      data?.inputValueType === undefined
    ) {
      setData({
        group: null,
        keyToCheck: '',
        keyToCompare: '',
        operator: '',
        valueToCompare: '',
        selectedDevice: '',
        inputValueType: false,
        error: true,
      });
    }

    if (data?.group?.id) {
      const foundGroup = deviceGroupsList.find((g) => g.id === data.group.id);
      setGroup(foundGroup ?? null);
    }

    if (data?.deviceType) {
      if (data.deviceType.includes('RescAlert Devices')) {
        setSystemType('RescAlert Devices');
        const types = data.deviceType.replace('RescAlert Devices ', '').split('_');
        setDeviceType(types[0]);
        setDeviceSubType(types[1]);
      } else {
        const systemTypeLocal = data.deviceType.split(' ')[0];
        const deviceTypeLocal = data.deviceType.split(' ')[1].split('_')[0];
        const subTypeLocal = data.deviceType.split(' ')[1].split('_')[1];
        setSystemType(systemTypeLocal);
        setDeviceType(deviceTypeLocal);
        setDeviceSubType(subTypeLocal);
      }
    }
  }, [deviceGroupsList]);

  useEffect(() => {
    setSystemType('Flood');
    setDeviceType('Stage');
  }, []);

  const updateData = (item) => {
    let nextItem = item;
    if (item.selectedDevice?.data?.length) {
      nextItem = {
        ...item,
        selectedDevice: {
          ...item.selectedDevice,
          data: [item.selectedDevice.data[0]],
        },
      };
    }
    const nextData = { ...data, ...nextItem };
    setData({
      ...nextData,
      error: verifyData(nextData),
    });
  };

  const verifyData = (obj) => {
    let error = false;
    if (
      obj.group === null ||
      obj.keyToCheck === '' ||
      (obj.keyToCompare === '' && obj.valueToCompare === '') ||
      (obj.keyToCompare === null && obj.valueToCompare === null) ||
      obj.operator === '' ||
      obj.operator === null
    ) {
      error = true;
      setError({ message: 'Action incomplete, check for missing fields.' });
    }
    if (!error) setError(null);
    return error;
  };

  const operators = [
    { operator: '==', types: ['all'] },
    { operator: '!=', types: ['all'] },
    { operator: '>', types: ['float'] },
    { operator: '<', types: ['float'] },
    { operator: '>=', types: ['float'] },
    { operator: '<=', types: ['float'] },
  ];

  // ---------- RENDER ----------
  return (
    <div className="d-flex flex-column w-100">
      {/* Toolbar row */}
      <div className="d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 bg-primary align-items-stretch align-items-md-center">
        {deviceGroupsList.length > 0 ? (
          <LabeledSelect
            label="Device Group:"
            value={group?.name || ''}
            onChange={(e) => {
              const selectedName = e.target.value;
              const foundGroup = deviceGroupsList.find((g) => g.name === selectedName);

              const controllerDeviceId = foundGroup?.controllers[0] ?? null;
              const device = connectedDevices.find((d) => d.id === controllerDeviceId) || null;

              setGroup(foundGroup || null);
              setDeviceType('');
              setDeviceSubType('');

              updateData({
                selectedDevice: device || null,
                group: foundGroup || null,
                keyToCheck: '',
                operator: '',
                keyToCompare: '',
              });
            }}
            className="flex-fill max-w-lg"
          >
            <option value="">-Select-</option>
            {deviceGroupsList.map((g) => (
              <option key={g.id} value={g.name}>
                {g.name}
              </option>
            ))}
          </LabeledSelect>
        ) : (
          <div>No Device Groups with Controllers available.</div>
        )}
        {group && (
          <div className="d-flex align-items-center">
            <Badge bg="light" text="dark" className="px-3 py-2 rounded-2">
              {group?.devices?.length ?? 0} Devices
            </Badge>
          </div>
        )}
      </div>

      {/* Value / Operator / Compare block */}
      {data.selectedDevice && (
        <div className="d-flex p-3 rounded-2 m-2 mt-0 bg-primary w-auto h-auto flex-column flex-md-row gap-3 align-items-stretch align-items-md-center">
          <div className="fw-medium align-self-start">Value:</div>

          <select
            className="bg-base p-1 w-100 w-md-50"
            onChange={(e) => updateData({ keyToCheck: e.target.value })}
            value={data.keyToCheck || ''}
          >
            <option value="">-Select-</option>
            {data.selectedDevice?.data.length > 0 &&
              Object.keys({
                ...data.selectedDevice?.data[0],
                ...data.selectedDevice?.device_settings,
              }).map((key) => {
                const map =
                  dataMaps[data.selectedDevice.system_type.toLowerCase()]?.[
                    data.selectedDevice.device_type.toLowerCase()
                  ];
                if (map?.[key]) {
                  return (
                    <option key={key} value={key}>
                      {map[key].name}
                    </option>
                  );
                }
                return null;
              })}
          </select>

          {data.keyToCheck && (
            <select
              className="bg-base p-1"
              style={{ maxWidth: 75 }}
              onChange={(e) => updateData({ operator: e.target.value })}
              value={data.operator || ''}
            >
              <option value="">Select</option>
              {operators.map((op) => {
                const map =
                  data.selectedDevice &&
                  dataMaps[data.selectedDevice.system_type.toLowerCase()]?.[
                    data.selectedDevice.device_type.toLowerCase()
                  ];
                const typeOfKey = map?.[data.keyToCheck]?.type;

                if (op.types.includes('all') || op.types.includes(typeOfKey)) {
                  return (
                    <option key={op.operator} value={op.operator}>
                      {op.operator === '==' ? '=' : op.operator}
                    </option>
                  );
                }
                return null;
              })}
            </select>
          )}

          {data.operator &&
            (data.inputValueType ? (
              <div className="flex-fill w-25">
                <DeviceCustomInput
                  value={data.valueToCompare}
                  onChange={(value) => updateData({ valueToCompare: value })}
                  dataMapItem={
                    dataMaps[data.selectedDevice.system_type.toLowerCase()][
                      data.selectedDevice.device_type.toLowerCase()
                    ][data.keyToCheck]
                  }
                />
              </div>
            ) : (
              <select
                className="bg-base p-1 w-25"
                onChange={(e) => updateData({ keyToCompare: e.target.value })}
                value={data.keyToCompare || ''}
              >
                <option value="">-Select-</option>
                {Object.keys({
                  ...data.selectedDevice?.data[0],
                  ...data.selectedDevice?.device_settings,
                }).map((key) => {
                  const map =
                    dataMaps[data.selectedDevice.system_type.toLowerCase()]?.[
                      data.selectedDevice.device_type.toLowerCase()
                    ];

                  if (map?.[key] && map[key].type === map[data.keyToCheck].type) {
                    return (
                      <option key={key} value={key}>
                        {map[key].name}
                      </option>
                    );
                  }
                  return null;
                })}
              </select>
            ))}

          {data.operator && (
            <Button
              variant="light"
              size="sm"
              className="rounded-2 text-nowrap w-auto"
              style={{ minWidth: 165 }}
              onClick={() =>
                updateData({
                  valueToCompare: '',
                  keyToCompare: '',
                  inputValueType: !data.inputValueType,
                })
              }
            >
              {data.inputValueType ? 'Change to compare' : 'Change to custom value'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceGroup;
